"use client";

import { useCallback, useRef, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import {
  formatPriceWithCode,
  type SupportedCurrency,
} from "@/lib/utils/currency";

interface DualRangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  step?: number;
  className?: string;
  minLabel?: string;
  maxLabel?: string;
  currencyCode?: SupportedCurrency;
}

export function DualRangeSlider({
  min,
  max,
  value,
  onChange,
  step = 1,
  className,
  minLabel,
  maxLabel,
  currencyCode,
}: DualRangeSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<"min" | "max" | null>(null);
  const latestValueRef = useRef<[number, number]>(value);
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    if (!draggingRef.current) {
      setLocalValue(value);
      latestValueRef.current = value;
    }
  }, [value]);

  const getValueFromPosition = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return min;
      const rect = trackRef.current.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const rawValue = min + ratio * (max - min);
      return Math.round(rawValue / step) * step;
    },
    [min, max, step],
  );

  const updateValue = useCallback(
    (newVal: number) => {
      setLocalValue((prev) => {
        let next: [number, number];
        if (draggingRef.current === "min") {
          const clamped = Math.min(newVal, prev[1] - step);
          next = [Math.max(min, clamped), prev[1]];
        } else {
          const clamped = Math.max(newVal, prev[0] + step);
          next = [prev[0], Math.min(max, clamped)];
        }
        latestValueRef.current = next;
        return next;
      });
    },
    [min, max, step],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, thumb: "min" | "max") => {
      e.preventDefault();
      e.stopPropagation();
      draggingRef.current = thumb;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      updateValue(getValueFromPosition(e.clientX));
    },
    [getValueFromPosition, updateValue],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return;
      e.preventDefault();
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      draggingRef.current = null;
      onChange(latestValueRef.current);
    },
    [onChange],
  );

  // listing(RealProductCard)의 PriceDisplay와 동일한 형식 사용 → "JPY 4,968"
  const formatPrice = (v: number) =>
    currencyCode ? formatPriceWithCode(v, currencyCode) : v.toLocaleString();

  const handleInputChange = (thumb: "min" | "max", raw: string) => {
    // 통화 기호 + 천 단위 구분자 모두 제거 후 숫자만 파싱
    const val = parseInt(raw.replace(/[^\d]/g, ""), 10);
    if (isNaN(val)) return;
    const next: [number, number] =
      thumb === "min"
        ? [Math.max(min, Math.min(val, localValue[1] - step)), localValue[1]]
        : [localValue[0], Math.min(max, Math.max(val, localValue[0] + step))];
    setLocalValue(next);
    latestValueRef.current = next;
    onChange(next);
  };

  const minPercent = ((localValue[0] - min) / (max - min)) * 100;
  const maxPercent = ((localValue[1] - min) / (max - min)) * 100;

  return (
    <div className={cn("flex flex-col gap-5", className)} data-vaul-no-drag>
      {/* 슬라이더 */}
      <div className="relative flex items-center w-full h-10 touch-none">
        <div
          ref={trackRef}
          className="relative w-full h-1.5 bg-gray-200 rounded-full"
        >
          <div
            className="absolute h-full bg-gray-800 rounded-full"
            style={{
              left: `${minPercent}%`,
              width: `${maxPercent - minPercent}%`,
            }}
          />
        </div>

        <div
          className="absolute w-6 h-6 bg-white border-2 border-gray-400 rounded-full shadow-md cursor-grab active:cursor-grabbing -translate-x-1/2 z-10"
          style={{ left: `${minPercent}%` }}
          onPointerDown={(e) => handlePointerDown(e, "min")}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          role="slider"
          aria-valuenow={localValue[0]}
          tabIndex={0}
        />

        <div
          className="absolute w-6 h-6 bg-white border-2 border-gray-400 rounded-full shadow-md cursor-grab active:cursor-grabbing -translate-x-1/2 z-10"
          style={{ left: `${maxPercent}%` }}
          onPointerDown={(e) => handlePointerDown(e, "max")}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          role="slider"
          aria-valuenow={localValue[1]}
          tabIndex={0}
        />
      </div>

      {/* 금액 입력 */}
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative">
          {minLabel && (
            <label className="absolute top-1.5 left-3 text-[10px] text-gray-400">
              {minLabel}
            </label>
          )}
          <input
            type="text"
            inputMode="numeric"
            value={formatPrice(localValue[0])}
            onChange={(e) => handleInputChange("min", e.target.value)}
            className="w-full pt-5 pb-2 px-3 border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:border-orange-400"
          />
        </div>
        <div className="text-gray-300">–</div>
        <div className="flex-1 relative">
          {maxLabel && (
            <label className="absolute top-1.5 left-3 text-[10px] text-gray-400">
              {maxLabel}
            </label>
          )}
          <input
            type="text"
            inputMode="numeric"
            value={formatPrice(localValue[1])}
            onChange={(e) => handleInputChange("max", e.target.value)}
            className="w-full pt-5 pb-2 px-3 border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:border-orange-400"
          />
        </div>
      </div>
    </div>
  );
}
